import type { ModelRow, OrgEndpointView, PurposeDefView } from "@/lib/api";
import { acceptsCustom, sourceLabel, TURNED_OFF_NOTE } from "@/lib/models";
import { plainVerdictNote } from "@/lib/probe-copy";
import type { PickerModel } from "./ModelParts";
import { endpointKind } from "./vendor-logos";

// The models a job's picker offers: enabled models of the job's role, your
// own models first (none for jobs that run on built-in models only). The
// current pick stays listed even when it was turned off, greyed out, so the
// picker can name it.
export function pickerModelsFor({
  purpose,
  rows,
  endpoints,
  current,
  customBlockedNote,
}: {
  purpose: Pick<PurposeDefView, "id" | "role" | "acceptsCustom">;
  rows: ModelRow[];
  endpoints: Array<Pick<OrgEndpointView, "name" | "label" | "baseUrl" | "lastProbe" | "keyNeeded">>;
  current?: string;
  // Set when this Boo can't run your own models yet.
  customBlockedNote?: string;
}): PickerModel[] {
  const custom: PickerModel[] = [];
  const hosted: PickerModel[] = [];
  const allowCustom = acceptsCustom(purpose);
  for (const r of rows) {
    if (r.role !== purpose.role || (!r.enabled && r.name !== current)) continue;
    if (r.source === "custom" && r.endpoint) {
      if (!allowCustom && r.name !== current) continue;
      const ep = endpoints.find((e) => e.name === r.endpoint);
      const verdict =
        ep?.lastProbe?.model === r.name || ep?.lastProbe?.model === r.name.slice(r.endpoint.length + 1)
          ? ep.lastProbe.verdicts.find((v) => v.purpose === purpose.id)
          : undefined;
      const blocked = !r.enabled || !allowCustom || !!customBlockedNote;
      custom.push({
        name: r.name,
        endpoint: r.endpoint,
        endpointLabel: ep ? sourceLabel(ep) : sourceLabel({ name: r.endpoint }),
        endpointKind: endpointKind(ep?.baseUrl, r.endpoint),
        disabled: blocked,
        note: !r.enabled
          ? TURNED_OFF_NOTE
          : !allowCustom
            ? "Built-in models only"
            : (customBlockedNote ?? (ep?.keyNeeded ? "Access key needed" : undefined)),
        summary: verdict ? (plainVerdictNote(purpose.id, verdict.note) ?? undefined) : undefined,
        verdict: verdict?.status,
      });
    } else {
      hosted.push({
        name: r.name,
        summary: r.summary,
        capabilities: r.capabilities,
        cost_tier: r.cost_tier,
        disabled: !r.enabled,
        note: r.enabled ? undefined : TURNED_OFF_NOTE,
      });
    }
  }
  return [...custom, ...hosted];
}

// The model to preselect after adding your own model from a job's picker:
// its first model of that job's role, never one the job can't use.
export function firstModelFor(
  endpoint: Pick<OrgEndpointView, "name" | "models">,
  purpose: Pick<PurposeDefView, "id" | "role" | "acceptsCustom">,
): string | null {
  if (!acceptsCustom(purpose)) return null;
  const m = endpoint.models.find((x) => x.role === purpose.role);
  return m ? `${endpoint.name}/${m.id}` : null;
}
